import { useRef, useState, type ReactNode } from "react";
import { useApiFetch } from "../../../api/client";
import { errorMessage, recordedLabel } from "../../cockpit/format";
import { PART_LABELS, partMeaning, type ReadsAsLookup } from "./designationParts";
import { GradeTdsFilesPanel } from "../tds/GradeTdsFilesPanel";
import { singleUploadLinks } from "../tds/tdsFiles";
import type { DesignationView, GradeLinkView, GradeTdsAttachResult } from "../types";

interface Props {
  designation: DesignationView;
  gradeLink: GradeLinkView | null;
  readsAsByCode: ReadsAsLookup;
  canWrite: boolean;
  onTdsChanged: () => void;
}

const SECTION_TITLE_CLASSES = "m-0 mb-1.5 text-[11px] font-semibold uppercase tracking-[0.07em] text-[#a9b0a1]";

function DetailList({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[12.5px]">
      {items.map(([label, value], itemIndex) => (
        <div key={itemIndex} className="contents">
          <dt className="text-[#a9b0a1]">{label}</dt>
          <dd className="m-0 wrap-break-word text-rs-off-white">{value || <span className="text-[#a9b0a1]">—</span>}</dd>
        </div>
      ))}
    </dl>
  );
}

export function GradeRowDetail({ designation, gradeLink, readsAsByCode, canWrite, onTdsChanged }: Props) {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);
  const [tdsReloadNumber, setTdsReloadNumber] = useState(0);

  const uploadChosenFile = async (file: File | undefined) => {
    if (!file || !gradeLink) return;
    setIsUploading(true);
    setUploadMessage(null);
    const formData = new FormData();
    formData.append("files", file);
    formData.append("links", singleUploadLinks(gradeLink.gradeId));
    try {
      const response = await apiFetchRef.current("nomenclature/grade-tds", { method: "POST", body: formData });
      const [result]: GradeTdsAttachResult[] = await response.json();
      setUploadMessage(
        result?.status === "AlreadyAttached" ? `${file.name} is already on this grade.` : `Attached ${file.name}.`,
      );
      setTdsReloadNumber((reloadNumber) => reloadNumber + 1);
      onTdsChanged();
    } catch (error) {
      setUploadMessage(`Couldn't attach ${file.name}: ${errorMessage(error)}`);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const sortedParts = [...designation.parts].sort((first, second) => first.partPosition - second.partPosition);
  const detailItems: [ReactNode, ReactNode][] = [
    ["Standard", designation.standardCode],
    ["Saved", recordedLabel(designation.createdUtc)],
    ["Grade added", gradeLink ? `${gradeLink.createdByDisplayName} · ${recordedLabel(gradeLink.createdUtc)}` : null],
    ["Supplier code", gradeLink?.supplierCode],
    ["Tradename code", gradeLink?.tradenameCode],
    ["Hardness", gradeLink?.hardnessText],
    ["As uploaded", gradeLink ? <code className="text-rs-green">{gradeLink.designationAsUploaded}</code> : null],
  ];
  const partItems: [ReactNode, ReactNode][] = sortedParts.map((part) => [
    <code key={part.partPosition} className="text-rs-green">
      {part.code}
    </code>,
    `${PART_LABELS[part.slotName]} · ${partMeaning(part, designation.standardCode, readsAsByCode)}`,
  ]);
  if (designation.suffixText) {
    partItems.push([<code className="text-rs-green">/{designation.suffixText}</code>, "Suffix · free text"]);
  }

  return (
    <div className="mb-1.5 grid gap-3 rounded-md border border-rs-jet border-l-2 border-l-rs-green/35 px-3.5 py-3 sm:grid-cols-[repeat(auto-fit,minmax(240px,1fr))]">
      <div>
        <h4 className={SECTION_TITLE_CLASSES}>Details</h4>
        <DetailList items={detailItems} />
      </div>
      <div>
        <h4 className={SECTION_TITLE_CLASSES}>Parts</h4>
        <DetailList items={partItems} />
      </div>
      <div className="flex flex-col gap-2 min-w-0">
        <h4 className={SECTION_TITLE_CLASSES}>TDS files</h4>
        {gradeLink ? (
          <>
            {uploadMessage && (
              <p className="m-0 text-xs text-rs-off-white" role="status">
                {uploadMessage}
              </p>
            )}
            <GradeTdsFilesPanel
              gradeId={gradeLink.gradeId}
              canWrite={canWrite}
              reloadNumber={tdsReloadNumber}
              onChanged={onTdsChanged}
            />
            {canWrite && (
              <div>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 bg-transparent border border-rs-jet-light rounded px-2.5 py-1 text-xs text-rs-off-white cursor-pointer hover:border-rs-green hover:text-rs-green disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isUploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <span className="material-symbols-sharp text-base! align-middle!" aria-hidden="true">
                    upload_file
                  </span>
                  {isUploading ? "Uploading…" : "Add TDS"}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  hidden
                  aria-label={`TDS file for ${gradeLink.tradename}`}
                  onChange={(event) => uploadChosenFile(event.target.files?.[0])}
                />
              </div>
            )}
          </>
        ) : (
          <p className="m-0 text-xs text-[#a9b0a1]">This RS code has no grades attached yet, so there's nothing to attach a TDS to.</p>
        )}
      </div>
    </div>
  );
}
